import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { toast } from "react-hot-toast";

export interface ISurgeryCardOption {
  id: string;
  name: string;
  type?: string;
  duration?: string | null;
  description?: string | null;
  details?: string | null;
  applicable_coupons?: {
    coupon_id: string;
    coupon_name: string;
    coupon_visibility?: boolean;
  }[];
}

interface ISurgeryOptionCardProps {
  isLoading: boolean;
  surgeryOption: ISurgeryCardOption | null;
  surgeryPrice: number;
  surgeryOriginalPrice: number;
  surgeryDiscountPercent: number;
  isSelected: boolean;
  formatAmount: (amount: number) => string;
  onAdd: (surgeryId: string, surgeryName: string) => void;
  onRemove: () => void;
  selectedCouponId?: string;
  onSelectCoupon?: (couponId: string | null | undefined, couponName: string | null | undefined) => void;
  fallbackLabel?: string;
}

const skeletonClass = "animate-pulse rounded bg-[#E9ECF2]";

const SurgeryOptionCard: React.FC<ISurgeryOptionCardProps> = ({
  isLoading,
  surgeryOption,
  surgeryPrice,
  surgeryOriginalPrice,
  surgeryDiscountPercent,
  isSelected,
  formatAmount,
  onAdd,
  onRemove,
  selectedCouponId,
  onSelectCoupon,
  fallbackLabel = "Surgery add-on",
}) => {
  if (isLoading) {
    return (
      <div className="mt-4 rounded-[10px] border border-[#E2E4EA] bg-[#F5F6F8] px-4 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="w-full space-y-2">
            <div className={`${skeletonClass} h-5 w-3/4`} />
            <div className={`${skeletonClass} h-4 w-1/2`} />
          </div>
          <div className="flex items-end gap-4">
            <div className="space-y-2">
              <div className={`${skeletonClass} h-5 w-20`} />
              <div className={`${skeletonClass} h-4 w-16`} />
            </div>
            <div className={`${skeletonClass} h-10 w-[84px] rounded-xl`} />
          </div>
        </div>
      </div>
    );
  }

  if (!surgeryOption) {
    return null;
  }

  // Discount comes straight from the API (cart-preview `discount_percent`); no
  // frontend calculation.
  const hasSurgeryDiscount = surgeryDiscountPercent > 0;

  const perSessionLabel = surgeryOption.duration
    ? `${surgeryOption.duration} min session`
    : surgeryOption.description || fallbackLabel;

  const coupons = surgeryOption.applicable_coupons || [];
  const couponItems = coupons.map((c) => ({
    id: c.coupon_id,
    title: c.coupon_name,
    state: { coupon_visibility: c.coupon_visibility },
  }));
  const defaultSelectedCoupon = selectedCouponId ? couponItems.find((item) => item.id === selectedCouponId) : undefined;

  return (
    <div
      className={`mt-4 rounded-[10px] px-[12px] py-[12px] ${
        isSelected ? "border border-[#B7BCC6] bg-[#EEF0F3]" : "border border-[#E2E2E2] bg-[#F7F8FA]"
      }`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch sm:justify-between">
        <div className="flex-1">
          <p className="text-[14px] font-medium text-[#222833]">{surgeryOption.name}</p>
          <p className="mt-2 text-[12px] font-medium text-[#6D6D6D]">{perSessionLabel}</p>
          {surgeryOption.details && (
            <ul className="mt-3 space-y-1 text-[12px] font-medium text-[#4B5162]">
              <li>{`• ${surgeryOption.details}`}</li>
            </ul>
          )}
        </div>
        <div className="flex w-full items-end justify-between gap-4 sm:w-auto sm:self-stretch sm:flex-col sm:items-end sm:justify-between">
          <div className="text-right">
            <p className="text-sm font-bold text-[#1F2431]">{formatAmount(surgeryPrice)}</p>
            {hasSurgeryDiscount && (
              <p className="text-sm text-[#8B92A1] line-through">{formatAmount(surgeryOriginalPrice)}</p>
            )}
          </div>
          {isSelected ? (
            <div className="mt-2 flex w-full items-center justify-end gap-2">
              <SearchableSelectMenu
                errorMessages={[]}
                fieldError={undefined}
                register={{} as any}
                selectItems={couponItems}
                onSelectItem={(item) => {
                  if (item) {
                    onSelectCoupon?.(item.id, item.title);
                  } else {
                    onSelectCoupon?.(null, null);
                  }
                }}
                filterOnEmptyQuery={(item) => item.state?.coupon_visibility !== false}
                onEnterPress={(typedValue) => {
                  if (!typedValue) return;
                  const exactMatch = coupons.find((c) => c.coupon_name?.toLowerCase() === typedValue.toLowerCase());
                  if (exactMatch) {
                    if (exactMatch.coupon_id === selectedCouponId) return;
                    onSelectCoupon?.(exactMatch.coupon_id, exactMatch.coupon_name);
                  } else {
                    toast.error("Coupon not applicable");
                  }
                }}
                placeholder={coupons.length === 0 ? "No Applicable Coupons" : "Select Coupon"}
                disabled={coupons.length === 0}
                defaultSelected={defaultSelectedCoupon}
                showDropdownIcon={true}
                withReset={true}
                listZIndex="z-50"
                className="text-[12px] font-medium text-[#3B4250] h-full py-0"
                inputContainerClassName="h-[35px] border-[#D8DBE2] rounded-[6px] bg-white text-[12px] flex items-center"
                containerClassName="w-[240px] min-w-[250px]"
              />
              <button
                type="button"
                onClick={onRemove}
                className="h-[30px] min-w-[74px] rounded-[6px] border border-[#D8DBE2] bg-white px-4 text-[12px] font-medium text-[#B4232C]"
              >
                Remove
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onAdd(surgeryOption.id, surgeryOption.name)}
              className="h-[30px] min-w-[74px] rounded-[6px] border border-[#D8DBE2] bg-white px-4 text-[12px] text-[#242933]"
            >
              Add
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurgeryOptionCard;
